import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { displayActionCardHeader } from 'modules/automation/utils/displayActionCardHeader';
import { FormLabel, TrackTime } from 'modules/automation/WorkflowBuilder/components';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import TaskAssignTo from './TaskAssignTo';
import TaskSubTask from './TaskSubTask';
import TaskReminder from './TaskReminder';
import PriorityDropdown from 'components/UI/PriorityDropdown/PriorityDropdown';
import { useFirmUserList } from 'hooks/useFirmUserList';
import { dueDateDropdownList, eventConfigOptions } from 'constants/automation';
import { SelectButton } from 'primereact/selectbutton';
import { InputSwitch } from 'primereact/inputswitch';

const AddTask = ({ index }) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext();

  const { userList, isFirmUserLoading } = useFirmUserList(true, false, true);
  const eventWithScheduleDate = watch(`actions[${index}].event_with_schedule_date`);
  const trigger = watch('trigger');

  return (
    <>
      {displayActionCardHeader('Task Configuration')}

      {trigger?.code === 'TRIG_EVENT_CREATE' && (
        <div className="row g-3 mb-2">
          <div className="col-12 col-lg-6">
            <FormLabel label="Create this task with due date based on trigger event scheduled date" />
            <Controller
              name={`actions[${index}].event_with_schedule_date`}
              defaultValue={false}
              control={control}
              render={({ field: { value, name, onChange } }) => (
                <InputSwitch checked={Boolean(value)} name={name} onChange={(e) => onChange(e.value)} className="input-swich" />
              )}
            />
          </div>
          {eventWithScheduleDate && (
            <div className="col-12 col-lg-6">
              <FormLabel label="Create this task before/after the trigger event" isRequired />
              <Controller
                name={`actions[${index}].event_preference`}
                control={control}
                defaultValue="event_before_schedule_date"
                rules={{ required: 'Required' }}
                render={({ field: { value, onChange } }) => (
                  <SelectButton
                    optionLabel="name"
                    optionValue="value"
                    value={value}
                    options={eventConfigOptions}
                    onChange={(e) => onChange(e.value)}
                  />
                )}
              />
              <RequiredValidation errors={errors} field={`actions.${index}.event_preference`} />
            </div>
          )}
        </div>
      )}

      <div className="row pt-2 g-3">
        {/* Task Name */}
        <div className="col-12 col-md-4">
          <FormLabel label="Task Name" isRequired />
          <Controller
            name={`actions.${index}.task_name`}
            control={control}
            rules={{ required: 'Required' }}
            render={({ field: { name, value, onChange } }) => (
              <InputText name={name} value={value} onChange={onChange} placeholder="Task Name" className="w-100 input-shadow" />
            )}
          />
          <RequiredValidation errors={errors} field={`actions.${index}.task_name`} />
        </div>

        {/* Priority */}
        <div className="col-12 col-md-4">
          <FormLabel label="Priority" isRequired />
          <Controller
            name={`actions.${index}.priority`}
            control={control}
            rules={{ required: 'Required' }}
            render={({ field: { name, value, onChange } }) => (
              <PriorityDropdown name={name} value={value} onChange={(e) => onChange(e.value)} className="w-100" placeholder="Select" />
            )}
          />
          <RequiredValidation errors={errors} field={`actions.${index}.priority`} />
        </div>

        {/* Task Assign To */}
        <div className="col-12 col-md-4">
          <TaskAssignTo {...{ userList, isFirmUserLoading, index }} />
        </div>

        {/* Task Subtask */}
        <div className="col-12">
          <TaskSubTask {...{ userList, isFirmUserLoading, index }} />
        </div>

        {/* Due Date (2 fields in one row) */}
        <div className="col-12 col-md-6">
          <FormLabel label="Due Date" isRequired />
          <div className="row g-2">
            <div className="col-6">
              <Controller
                name={`actions.${index}.due_date_cycle`}
                control={control}
                rules={{ required: 'Required' }}
                render={({ field: { name, value, onChange } }) => (
                  <InputText
                    name={name}
                    value={value}
                    onChange={onChange}
                    keyfilter="int"
                    min={0}
                    placeholder="Type Count"
                    className="input-shadow input-height w-100"
                  />
                )}
              />
              <RequiredValidation errors={errors} field={`actions.${index}.due_date_cycle`} />
            </div>
            <div className="col-6">
              <Controller
                name={`actions.${index}.due_date_period`}
                control={control}
                rules={{ required: 'Required' }}
                render={({ field: { name, value, onChange, ref } }) => (
                  <Dropdown
                    name={name}
                    value={value}
                    options={dueDateDropdownList}
                    onChange={onChange}
                    placeholder="Select"
                    className="input-height w-100"
                    inputRef={ref}
                  />
                )}
              />
              <RequiredValidation errors={errors} field={`actions.${index}.due_date_period`} />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="col-12">
          <FormLabel label="Description" />
          <Controller
            name={`actions.${index}.description`}
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <TextSnippetWrapper
                type="textarea"
                name={name}
                value={value}
                className="w-100 input-shadow"
                onChange={onChange}
                placeholder="Description"
              />
            )}
          />
        </div>

        {/* Task Reminder */}
        <div className="col-12">
          <TaskReminder index={index} optionText="before due date" />
        </div>

        {/* Track Time */}
        <div className="col-12">
          <TrackTime index={index} />
        </div>
      </div>
    </>
  );
};

export default AddTask;
